import { Box, Flex } from '@chakra-ui/react';
import {
  ZoomOut,
  ZoomIn,
  Upload01,
  Download01,
  Printer,
  Expand03,
  DotsVertical,
} from '@kula-ai/icons-core';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { useRef } from 'react';
import type { ReactNode } from 'react';

interface DocViewerToolbarProps {
  onDownload: () => void;
  onReplace: (files: File[]) => void;
  acceptedFiles?: string[];
  isReplaceLoading?: boolean;
}

const DisabledIcon = ({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="28px"
    h="28px"
    borderRadius="4px"
    opacity={0.35}
    cursor="not-allowed"
    aria-label={label}
    aria-disabled="true"
  >
    {children}
  </Box>
);

const ActiveIcon = ({
  children,
  label,
  onClick,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Box
          as="button"
          type="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="28px"
          h="28px"
          borderRadius="4px"
          cursor="pointer"
          aria-label={label}
          _hover={{ bg: 'secondary-50' }}
          onClick={onClick}
        >
          {children}
        </Box>
      </TooltipTrigger>
      <TooltipContent side="top" collisionPadding={12}>
        {label}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const VSep = () => (
  <Box alignSelf="center" w="1px" h="16px" bg="secondary-200" flexShrink={0} />
);

const ToolGroup = ({ children }: { children: ReactNode }) => (
  <Flex
    align="center"
    border="1px solid"
    borderColor="secondary-200"
    borderRadius="6px"
    px="2px"
    gap="0"
    h="32px"
  >
    {children}
  </Flex>
);

export const DocViewerToolbar = ({
  onDownload,
  onReplace,
  acceptedFiles = ['.docx'],
  isReplaceLoading = false,
}: DocViewerToolbarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReplaceClick = () => {
    if (!isReplaceLoading) fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length) onReplace(files);
    e.target.value = '';
  };

  return (
    <Flex align="center" gap="4px" p="3px 6px" bg="white" h="40px">
      {/* Zoom controls — all disabled */}
      <ToolGroup>
        <DisabledIcon label="Zoom out">
          <ZoomOut size={18} />
        </DisabledIcon>
        <VSep />
        <Box
          px="8px"
          fontSize="13px"
          color="secondary-700"
          opacity={0.35}
          cursor="not-allowed"
          whiteSpace="nowrap"
        >
          Page Width
        </Box>
        <VSep />
        <DisabledIcon label="Zoom in">
          <ZoomIn size={18} />
        </DisabledIcon>
      </ToolGroup>

      {/* Actions: Replace ✓, Download ✓, Print ✗, Fullscreen ✗ */}
      <ToolGroup>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFiles.join(',')}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <ActiveIcon label="Replace" onClick={handleReplaceClick}>
          <Upload01 size={18} />
        </ActiveIcon>
        <VSep />
        <ActiveIcon label="Download" onClick={onDownload}>
          <Download01 size={18} />
        </ActiveIcon>
        <VSep />
        <DisabledIcon label="Print">
          <Printer size={18} />
        </DisabledIcon>
        <VSep />
        <DisabledIcon label="Fullscreen">
          <Expand03 size={18} />
        </DisabledIcon>
        <VSep />
        <DisabledIcon label="More options">
          <DotsVertical size={18} />
        </DisabledIcon>
      </ToolGroup>
    </Flex>
  );
};
